import React, { useState, useRef, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip, Snackbar, Typography, Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { sparkleStyles } from '../styles/common.styles';
import TypeWriterEffect from '../utils/TypeWriterEffect';
import ReactMarkdown from 'react-markdown';
import { fetchTextToSpeech } from '../utils/textToSpeech';
import { ReCaptchaContext } from './recatcha-provider';
import ReactPlayer from 'react-player';

function CommonDialog({ open, onClose, title, content, shouldTypeAhead = false, isMarkdown = false, showTTSButton = false }) {
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const playerRef = useRef(null);
  const contentRef = useRef(null);
  const { captchaVerified, onCaptchaVerify } = useContext(ReCaptchaContext);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setShowCopyMessage(true);
  };

  const handleCloseSnackbar = () => {
    setShowCopyMessage(false);
  };

  const handlePlayTTS = async () => {
    if (!content) return;

    if (captchaVerified) {
      await playTTS();
      return;
    }

    if (process.env.NODE_ENV === 'production') {
      setTtsLoading(true);
      const status = await onCaptchaVerify();
      if (status) {
        await playTTS();
      }
      setTtsLoading(false);
    } else {
      await playTTS();
    }
  };

  const playTTS = async () => {
    setTtsLoading(true);
    try {
      // Get the text content from the rendered markdown
      const textContent = contentRef.current?.textContent || content;
      const audioData = await fetchTextToSpeech(textContent);
      const audioBlob = new Blob([audioData], { type: 'audio/mp3' });
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
    } catch (err) {
      alert('Failed to generate audio: ' + err.message);
      setTtsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setAudioUrl(null);
    setTtsLoading(false);
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleDialogClose} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 7 }}>
          {title}
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', right: 8, top: 8, gap: 1 }}>
            {showTTSButton && (
              <Tooltip title="Generate Audio">
                <span>
                  <IconButton
                    onClick={handlePlayTTS}
                    disabled={!content || ttsLoading || audioUrl}
                    sx={{
                      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                      boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                      transition: 'all 0.3s ease',
                      minWidth: 40,
                      minHeight: 40,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'scale(1.08)',
                        boxShadow: '0 4px 8px 3px rgba(33, 203, 243, .4)',
                      },
                      '&.Mui-disabled': {
                        background: 'linear-gradient(45deg, #ccc 30%, #ddd 90%)',
                        boxShadow: 'none',
                        color: '#aaa',
                      },
                      p: 0
                    }}
                  >
                    <AutoAwesome sx={ttsLoading ? sparkleStyles : {}} fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Copy content">
              <IconButton 
                onClick={handleCopy}
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div ref={contentRef}>
            {shouldTypeAhead ? (
              <TypeWriterEffect text={content} speed={30} />
            ) : (
              isMarkdown ? (
                <ReactMarkdown>{content}</ReactMarkdown>
              ) : (
                <Typography variant="body1">{content}</Typography>
              )
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 2, p: 2 }}>
          {showTTSButton && audioUrl && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ width: '100%', height: 50 }}>
                <ReactPlayer
                  ref={playerRef}
                  url={audioUrl}
                  width="100%"
                  height="100%"
                  controls
                  playing
                  onReady={() => setTtsLoading(false)}
                  onError={(e) => {
                    alert('Audio playback failed: ' + e);
                    setTtsLoading(false);
                  }}
                  config={{
                    file: {
                      attributes: {
                        controlsList: 'nodownload',
                        style: {
                          width: '100%',
                          height: '100%'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={showCopyMessage}
        autoHideDuration={1000}
        onClose={handleCloseSnackbar}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

export default CommonDialog; 